import { defineStore, storeToRefs } from 'pinia';
import type { Book as ReadingProfileBook, ReadingTimeStatsResponse } from '@ilteducation/reading-profile-types';
import type { Ref } from 'vue';
import {
    Book,
    LibraryBook,
    Message,
    MessageCreate,
    Pupil,
    PupilTask,
    PupilTaskOverview,
    PupilTaskSubmission,
    PupilTeacher,
    School,
} from '@ilteducation/giglets-types';

export const usePupilStore = defineStore('pupil', () => {
    const pupil = ref<Pupil>();
    const book = ref<Book>();
    const books = ref<LibraryBook[]>([]);
    const school = ref<School>();
    const messages = ref<Message[]>([]);
    const tasks = ref<PupilTaskOverview[]>([]);
    const teachers = ref<PupilTeacher[]>([]);
    const readingProfileBooks = ref<{ data: ReadingProfileBook[] }>({ data: [] });
    const userReadingTimeStats = ref<ReadingTimeStatsResponse>();
    const bookSessionId = ref<string | undefined>(undefined);
    const isLoaded = ref<Record<string, boolean>>({});
    const isLoading = ref<Record<string, boolean>>({});

    const state = {
        pupil,
        book,
        books,
        school,
        messages,
        tasks,
        teachers,
        bookSessionId,
        readingProfileBooks,
        userReadingTimeStats,
    };

    const { readingProfileApiUrl } = useEnv();
    const auth = useAuth();
    const { accessToken } = storeToRefs(auth);

    function fetchPupil(): Promise<Ref<Pupil>> {
        return load('/api/pupil/current', 'pupil', pupil);
    }
    function reloadPupil(): Promise<Ref<Pupil>> {
        isLoaded.value.pupil = false;
        return fetchPupil();
    }
    function fetchBook(): Promise<Ref<Book>> {
        return load('/api/pupil/current/book', 'book', book);
    }
    function fetchBooks(): Promise<Ref<LibraryBook[]>> {
        return load('/api/pupil/current/books', 'books', books);
    }
    function fetchSchool(): Promise<Ref<School>> {
        return load('/api/pupil/current/school', 'school', school);
    }
    function fetchMessages(): Promise<Ref<Array<Message>>> {
        return load('/api/pupil/current/messages', 'messages', messages);
    }
    function fetchTasks(): Promise<Ref<Array<PupilTaskOverview>>> {
        return load('/api/pupil/current/tasks_overview', 'tasks', tasks);
    }
    function fetchTeachers(): Promise<Ref<Array<PupilTeacher>>> {
        return load('/api/pupil/current/teachers', 'teachers', teachers);
    }
    function fetchReadingProfileBooks(): Promise<Ref<{ data: ReadingProfileBook[] }>> {
        return load(`${readingProfileApiUrl}/read-books`, 'readingProfileBooks', readingProfileBooks);
    }
    function fetchUserReadingTimeStats(): Promise<Ref<ReadingTimeStatsResponse>> {
        return load(`${readingProfileApiUrl}/time`, 'userReadingTimeStats', userReadingTimeStats);
    }

    async function updatePupil(newData: Partial<Pupil>): Promise<Ref<Pupil | undefined>> {
        pupil.value = await $fetch<Pupil>('/api/pupil/current', {
            method: 'PUT',
            body: newData,
        });
        return pupil;
    }

    async function updateCurrentBook(id: number) {
        await updatePupil({ book: { id } });
        isLoaded.value.book = false;
    }

    async function createMessage(msg: MessageCreate) {
        const newMessage: Message = await $fetch('/api/pupil/current/messages', {
            method: 'POST',
            body: msg,
        });
        if (messages.value) {
            messages.value.push(newMessage);
        } else {
            messages.value = [newMessage];
        }
    }

    async function markMessageRead(messageId: number) {
        await $fetch('/api/pupil/current/messages/' + messageId + '/read', {
            method: 'PUT',
        });
        const msg = messages.value?.find((m) => m.id === messageId);
        if (msg) {
            msg.msg_read = true;
        }
    }

    async function updateTask(pupilTaskId: number, task: PupilTaskSubmission) {
        const updated: PupilTask = await $fetch('/api/pupil/current/tasks/' + pupilTaskId, {
            method: 'PUT',
            body: task,
        });
        const t = tasks.value?.find((m) => m.id === pupilTaskId);
        if (t) {
            Object.assign(t, updated);
        }
        return updated;
    }

    async function load<T>(url: string, name: keyof typeof state, ref: Ref<T | undefined>): Promise<Ref<T>> {
        if (isLoaded.value[name]) {
            return Promise.resolve(ref as Ref<T>);
        }
        if (isLoading.value[name]) {
            return new Promise<Ref<T>>((resolve) => {
                const unwatch = watch(isLoaded.value, () => {
                    if (isLoaded.value[name]) {
                        unwatch();
                        resolve(ref as Ref<T>);
                    }
                });
            });
        }
        try {
            isLoading.value[name] = true;
            ref.value = await $fetch(url, {
                headers: {
                    Authorization: `Bearer ${accessToken.value}`,
                },
            });
            isLoaded.value[name] = true;
            return Promise.resolve(ref as Ref<T>);
        } finally {
            isLoading.value[name] = false;
        }
    }

    function teacherName(uid: string | { uid: string } | undefined): string {
        if (!uid) {
            return '';
        }
        const teacherUid = typeof uid === 'string' ? uid : uid.uid;
        return teachers.value.find((t) => t.uid === teacherUid)?.name || '';
    }

    return {
        ...state,
        isLoading,
        isLoaded,
        fetchPupil,
        reloadPupil,
        fetchBook,
        fetchBooks,
        fetchSchool,
        fetchMessages,
        fetchTasks,
        fetchTeachers,
        fetchReadingProfileBooks,
        fetchUserReadingTimeStats,
        updatePupil,
        updateCurrentBook,
        createMessage,
        markMessageRead,
        updateTask,
        teacherName,
    };
});
